import { useClipboard } from '@vueuse/core';
import dayjs from 'dayjs';
import useUrlReplace from '~/composables/useUrlReplace';
import { useArticleStore } from '~/store/article';
import { getOneId } from '~/utils/utils';

export interface DefaultShareInfo {
    numberOfMonthToolTipViewed: number;
    lastUpdateDate: number;
    hasSharingAccess: boolean;
    toolTipViewCount: number;
    currentToolTipCountMonth: number;
}
const showShareModal = ref(false);
const hasSharingAccess = ref(false);
const shareCount = ref({
  total: 0,
  remaining: 0
});
const shareUrl = ref('');
const previewUrl = ref('');
const hasShareAbleUrl = ref(false);

export default function useArticleGiveAway () {
  const getHasTotalProductFromOneidInfo = () => {
    try {
      const subUserData = localStorage.getItem('sub-user-data');
      if (!subUserData) { return false; }
      const parsed = JSON.parse(subUserData);
      return parsed?.value?.has_total === true;
    } catch (e) {
      return false;
    }
  };
  const getTotalShareCount = () => {
    return window._nhstFeatures.config.giftArticleShareCount;
  };
  const openShareModal = async () => {
    await loadWindowNhstFeatures();
    try {
      if (!window._nhstFeatures) {
        throw new Error('window._nhstFeatures is not available');
      }
      const response = await window._nhstFeatures.serviceFactory.getDefaultAuthServices().zephrPublicClient.tokenGiftState();
      if (response.status >= 200 && response.status <= 299) {
        shareCount.value.total = getTotalShareCount();
        shareCount.value.remaining = response.data.remainingAllowance || 0;
        showShareModal.value = true;
      }
    } catch (e) {
      throw createError('Error in getting share related information ');
    }
  };
  const loadWindowNhstFeatures = async () => {
    let tries = 10;
    while (!window._nhstFeatures && tries > 0) {
      // eslint-disable-next-line no-console
      console.log('Waiting until window._nhstFeatures is loaded');
      await new Promise(resolve => setTimeout(resolve, 1000));
      tries--;
    }
  };
  const userUniqueKey = () => {
    const oneId = getOneId();
    if (!oneId) {
      // eslint-disable-next-line no-console
      console.warn('one_id not found in sub-user-data');
      return '';
    }
    return `dn_share-${oneId}`;
  };

  const disableGenButton = ref(false);

  const setShareArticleInfo = () => {
    const date = dayjs();
    const defaultShareInfo: DefaultShareInfo = {
      numberOfMonthToolTipViewed: 0,
      lastUpdateDate: -1,
      hasSharingAccess: false,
      toolTipViewCount: 0,
      currentToolTipCountMonth: -1
    };
    const shareKey = userUniqueKey();
    const shareInfo = shareKey ? window.localStorage.getItem(shareKey) : null;
    const shareInfoParsed: DefaultShareInfo = shareInfo ? JSON.parse(shareInfo) as DefaultShareInfo : defaultShareInfo;

    if (shareInfoParsed.lastUpdateDate !== date.get('date')) {
      if (getHasTotalProductFromOneidInfo() !== null) {
        const hasTotalProduct = getHasTotalProductFromOneidInfo();
        hasSharingAccess.value = hasTotalProduct;
        if (shareInfoParsed.currentToolTipCountMonth !== date.get('month')) {
          shareInfoParsed.numberOfMonthToolTipViewed += 1;
          shareInfoParsed.currentToolTipCountMonth = date.get('month');
          shareInfoParsed.toolTipViewCount = 0;
        }
        const newShareInfo = {
          lastUpdateDate: date.get('date'),
          hasSharingAccess: hasTotalProduct,
          toolTipViewCount: shareInfoParsed.toolTipViewCount,
          currentToolTipCountMonth: shareInfoParsed.currentToolTipCountMonth,
          numberOfMonthToolTipViewed: shareInfoParsed.numberOfMonthToolTipViewed || 1
        };
        window.localStorage.setItem(shareKey, JSON.stringify(newShareInfo));
      }
    } else {
      hasSharingAccess.value = shareInfoParsed.hasSharingAccess;
    }
  };

  const getShareAbleUrl = async () => {
    disableGenButton.value = true;
    await loadWindowNhstFeatures();
    try {
      if (!window._nhstFeatures) {
        throw new Error('window._nhstFeatures is not available');
      }
      const rtConfPub = useRuntimeConfig().public;
      const articleApi = import.meta.client && rtConfPub.article?.clientApi ? rtConfPub.article?.clientApi : rtConfPub.article?.api;
      const articleStore = useArticleStore();
      const id = articleStore.currentArticle?.id;
      const { replaceUrl } = useUrlReplace();
      const relativeApi = articleApi.replace(/^(?:\/\/|[^/]+)*\//, '/');
      const articleApiWithId = replaceUrl(relativeApi, { id });

      const response = await window._nhstFeatures.serviceFactory.getDefaultAuthServices().createGiftToken(articleApiWithId);

      if (response.status >= 200 && response.status <= 299) {
        hasShareAbleUrl.value = true;
        shareCount.value.remaining -= 1;
        shareUrl.value = useRequestURL().protocol + '//' + window.location.host + window.location.pathname + `?${response.data.pathWithToken.split('?')[1]}`;
        previewUrl.value = `${shareUrl.value.substring(0, 60)}..`;
      } else {
        await reOpenModal();
      }
    } catch (e) {
      throw createError('Error in creating gift token ');
    } finally {
      disableGenButton.value = false;
    }
  };
  const reOpenModal = async () => {
    showShareModal.value = false;
    await openShareModal();
  };
  const { copy } = useClipboard();

  const shareArticle = async () => {
    await copy(shareUrl.value);
  };

  const closeShareModal = () => {
    showShareModal.value = false;
    hasShareAbleUrl.value = false;
  };

  const showUrlGenerateButton = computed(() => {
    return shareCount.value.remaining > 0 && !hasShareAbleUrl.value;
  });

  return {
    closeShareModal,
    showUrlGenerateButton,
    openShareModal,
    hasSharingAccess,
    previewUrl,
    shareCount,
    hasShareAbleUrl,
    getShareAbleUrl,
    shareArticle,
    showShareModal,
    setShareArticleInfo
  };
}
